import {
    getQueryByEditorial,
    getQueryBycategoryFromPRO
} from '../editoriaUtils'

export const LTE_NOW = {
    lte: 'now'
}

export const ORDER_DESC = {
    'source.published_at': {
        order: 'desc'
    },
    _score: {
        order: 'desc'
    }
}

/**
 * getPeriod
 * @param {string} period
 * @return {string}
 */
export const getPeriod = (period: string) => {
    const actualDate = new Date()

    const actualMonth = `${actualDate.getFullYear()}-${
        actualDate.getMonth() + 1
    }`

    const previousMonth = `${actualDate.getFullYear()}-${actualDate.getMonth()}`

    switch (period) {
        case 'todos':
            return LTE_NOW
        case 'hoje':
            return {
                gt: `now-1d/d`,
                lte: `now/d`
            }
        case 'ontem':
            return {
                gte: `now-1d/d`,
                lt: `now/d`
            }
        case 'ultimos7dias':
            return {
                gte: `now-7d/d`
            }
        case 'ultimos30dias':
            return {
                gte: `now-31d/d`
            }
        case 'esteMes':
            return {
                gte: `${actualMonth}-01`,
                lte: `${actualMonth}-30`,
                format: 'yyyy-MM-dd'
            }
        case 'mesPassado':
            return {
                gt: `${previousMonth}-01`,
                lte: `${previousMonth}-30`,
                format: 'yyyy-MM-dd'
            }
        default:
            return LTE_NOW
    }
}

/**
 * Get Order
 * @param {string} ordenation
 * @return {object}
 */
export const getOrder = (ordenation: string) => {
    switch (ordenation) {
        case 'more-relevant':
            return {
                _score: {
                    order: 'desc'
                },
                'source.published_at': {
                    order: 'desc'
                }
            }
        case 'more-recent':
            return ORDER_DESC
        case 'older':
            return {
                'source.published_at': {
                    order: 'asc'
                },
                _score: {
                    order: 'desc'
                }
            }

        default:
            return ORDER_DESC
    }
}

/**
 * getFirstItemNumber
 * @param {number} page
 * @return {number}
 */
export const getFirstItemNumber = (page: number) => {
    page = page - 1
    return page ? page * 10 : 0
}

/**
 * getLastItemNumber
 * @param {number} page
 * @return {number}
 */
export const getLastItemNumber = (page: number) => {
    page = page - 1
    return page ? page * 10 + 10 - 1 : 10
}

/**
 * Get term by url path
 * @param {string} term
 * @param {'periodo' | 'ordenacao' | 'page'} flag
 * @return {string | null}
 */
export const getTermByURLPath = (
    term: string,
    flag: 'periodo' | 'ordenacao' | 'page'
) => {
    const output = term?.includes(flag)
        ? term
              .split('&')
              .filter((item: any) => item.includes(flag) && item)[0]
              .split('=')[1]
        : null

    return output
}

/**
 * Is Term Enclosed by Quotes (simple or double)
 * @param {string} term
 * @return {boolean}
 */
export const isTermEnclosedByQuotes = (term: string) => {
    if (
        (term?.startsWith("'") && term?.endsWith("'")) ||
        (term?.startsWith('"') && term?.endsWith('"'))
    ) {
        return true
    }
    return false
}

/**
 * Search by exact term
 * @param {string} textTerm
 * @param {any} periodo
 * @return {object}
 */
export const searchByExactTerm = (textTerm: string, periodo: any) => {
    if (isTermEnclosedByQuotes(textTerm)) {
        return {
            bool: {
                must: [
                    {
                        multi_match: {
                            query: textTerm.slice(1, -1),
                            type: 'phrase',
                            fields: ['title.headline', 'content.plain']
                        }
                    },
                    {
                        range: periodRange(periodo)
                    }
                ],
                filter: [
                    {
                        term: {
                            'source.recycled': false
                        }
                    }
                ]
            }
        }
    }
}

/**
 * Period Range
 * @param {any} periodo
 * @return {object}
 */
export const periodRange = (periodo: any) => {
    if (periodo) {
        return {
            'source.published_at': getPeriod(periodo)
        }
    } else {
        return {
            'source.published_at': {
                lte: 'now'
            }
        }
    }
}

/**
 * Search Ordenation
 * @param {any} ordenacao
 * @return {object[]}
 */
export const searchOrdenation = (ordenacao: any) => {
    if (ordenacao) {
        return [getOrder(ordenacao)]
    } else {
        return [
            {
                'source.published_at': {
                    order: 'desc'
                },
                _score: {
                    order: 'desc'
                }
            }
        ]
    }
}

/**
 * Open Search - Normal Search
 * @param {string} textTerm
 * @param {any} periodo
 * @return {object}
 */
export const defaultSearch = (textTerm: string, periodo: any) => {
    if (textTerm?.toLowerCase() === 'pro') {
        return {
            match: {
                'source.type': 'cms.newsletter'
            }
        }
    }
    if (textTerm && !isTermEnclosedByQuotes(textTerm)) {
        return {
            bool: {
                must: [
                    {
                        multi_match: {
                            query: textTerm,
                            operator: 'AND',
                            fields: [
                                'title.headline',
                                'title.hat',
                                'title.subhead',
                                'content.plain'
                            ]
                        }
                    },
                    {
                        term: {
                            'source.recycled': false
                        }
                    },
                    {
                        range: periodRange(periodo)
                    }
                ]
            }
        }
    }
}

/**
 * Get Params
 * @param {string} term
 * @param {any} router
 * @param {any} typeValue
 * @param {int} page
 * @param {'ordenacao' | 'periodo' | null} type
 * @return {string}
 */
export const redirectUrlwithParams = (
    term: string | null,
    router: any,
    typeValue: any,
    page?: number,
    type: 'ordenacao' | 'periodo' | null = null
): string => {
    const queryData = (router?.query?.term as string)
        .split('&')
        .map((item) => item.split('='))

    let periodo =
        type == 'periodo'
            ? typeValue
            : queryData.filter((item) => item[0] == 'periodo')[0]
    let ordenacao =
        type == 'ordenacao'
            ? typeValue
            : queryData.filter((item) => item[0] == 'ordenacao')[0]
    let pageTemp = page ?? queryData.filter((item) => item[0] == 'page')[0]

    if (type == 'periodo') {
        pageTemp = 1
    }

    // is array check?
    periodo = Array.isArray(periodo) ? periodo[1] : periodo
    ordenacao = Array.isArray(ordenacao) ? ordenacao[1] : ordenacao

    const output =
        `${term}` +
        (ordenacao ? `&ordenacao=${ordenacao}` : '') +
        (periodo ? `&periodo=${periodo}` : '') +
        (page ? `&page=${pageTemp}` : '')

    return output
}

/**
 * Get results from search
 * Description: Make the query for opensearch
 * @param {string} term
 * @param {string} fullURL
 * @return {Promise}
 */
export const getResultsFromSearch = async (term: string, fullURL: string) => {
    return new Promise((resolve, reject) => {
        if (
            !process?.env?.NEXT_PUBLIC_OPENSEARCH ||
            !process?.env?.NEXT_PUBLIC_OPENSEARCH_TOKEN
        ) {
            reject(new Error('Falhou'))
            return
        }

        const periodo = getTermByURLPath(fullURL, 'periodo')
        const ordenacao = getTermByURLPath(fullURL, 'ordenacao')
        const pagination = getTermByURLPath(fullURL, 'page')

        const paginationRange = {
            from: pagination ? getFirstItemNumber(parseInt(pagination)) : 0,
            size: 10
        }
        const searchQuery = JSON.stringify({
            from: paginationRange.from,
            size: paginationRange.size,
            query: isTermEnclosedByQuotes(term)
                ? searchByExactTerm(term, periodo)
                : defaultSearch(term, periodo),
            sort: searchOrdenation(ordenacao),
            highlight: {
                pre_tags: [''],
                post_tags: [''],
                fields: {
                    'content.plain': {}
                }
            }
        })

        const searchDataAPI = fetch(process?.env?.NEXT_PUBLIC_OPENSEARCH, {
            method: 'post',
            headers: new Headers({
                Authorization: process.env?.NEXT_PUBLIC_OPENSEARCH_TOKEN,
                'Content-Type': 'application/json'
            }),
            body: searchQuery
        })

        searchDataAPI.then((response) => resolve(response.json()))
    })
}

/**
 * @param {string} paramsUrl
 * @return {Promise}
 */
export const getProPostsFromOpenSearch = async (paramsUrl: string) => {
    return new Promise((resolve, reject) => {
        if (
            !process?.env?.NEXT_PUBLIC_OPENSEARCH ||
            !process?.env?.NEXT_PUBLIC_OPENSEARCH_TOKEN
        ) {
            reject(new Error('Falhou'))
            return
        }

        const pagination = paramsUrl.includes('page')
            ? paramsUrl.split('&')[1].split('=')[1]
            : null

        const page = pagination ? parseInt(pagination) : 1

        const paginationRange = {
            from: (page - 1) * 10,
            size: 10
        }

        const defaultSearch = () => {
            return {
                match: {
                    'source.type': 'cms.newsletter'
                }
            }
        }

        const searchQuery = JSON.stringify({
            from: paginationRange.from,
            size: paginationRange.size,
            query: defaultSearch(),
            sort: [
                {
                    'source.published_at': {
                        order: 'desc'
                    },
                    _score: {
                        order: 'desc'
                    }
                }
            ],
            highlight: {
                pre_tags: [''],
                post_tags: [''],
                fields: {
                    'content.plain': {}
                }
            }
        })

        const searchDataAPI = fetch(process?.env?.NEXT_PUBLIC_OPENSEARCH, {
            method: 'post',
            headers: new Headers({
                Authorization: process.env?.NEXT_PUBLIC_OPENSEARCH_TOKEN,
                'Content-Type': 'application/json'
            }),
            body: searchQuery
        })

        searchDataAPI.then((response) => resolve(response.json()))
    })
}

const InfoCategories = ['stf', 'executivo', 'legislativo', 'justica']

/**
 * @param {string} verticalFromParams
 * @param {string} category
 * @param {number} page
 * @return {Promise}
 */
export const getPostsByEditorial = async (
    verticalFromParams: string,
    category?: any,
    page?: number
) => {
    return new Promise((resolve, reject) => {
        if (
            !process?.env?.NEXT_PUBLIC_OPENSEARCH ||
            !process?.env?.NEXT_PUBLIC_OPENSEARCH_TOKEN
        ) {
            reject(new Error('Falhou'))
            return
        }

        const currentPage = page ? page : 1

        const paginationRange = {
            from: (currentPage - 1) * 10,
            size: 10
        }

        const getProOrNotQuery = (vertical: any, category?: any) => {
            if (category !== undefined && !InfoCategories.includes(category)) {
                const searchQuery = JSON.stringify({
                    from: paginationRange.from,
                    size: paginationRange.size,
                    query: {
                        bool: {
                            should: getQueryBycategoryFromPRO(
                                vertical,
                                category
                            ),
                            minimum_should_match: 1
                        }
                    },
                    sort: [
                        {
                            'source.published_at': {
                                order: 'desc'
                            }
                        }
                    ],
                    highlight: {
                        pre_tags: [''],
                        post_tags: [''],
                        fields: {
                            'content.plain': {}
                        }
                    }
                })
                return searchQuery
            } else {
                const searchQuery = JSON.stringify({
                    from: paginationRange.from,
                    size: paginationRange.size,
                    query: {
                        bool: {
                            should: getQueryByEditorial(vertical, category),
                            minimum_should_match: 1
                        }
                    },
                    sort: [
                        {
                            'source.published_at': {
                                order: 'desc'
                            },
                            _score: {
                                order: 'desc'
                            }
                        }
                    ],
                    highlight: {
                        pre_tags: [''],
                        post_tags: [''],
                        fields: {
                            'content.plain': {}
                        }
                    }
                })

                return searchQuery
            }
        }

        const searchDataAPI = fetch(process?.env?.NEXT_PUBLIC_OPENSEARCH, {
            method: 'post',
            headers: new Headers({
                Authorization: process.env?.NEXT_PUBLIC_OPENSEARCH_TOKEN,
                'Content-Type': 'application/json'
            }),
            body: getProOrNotQuery(verticalFromParams, category)
        })

        searchDataAPI.then((response) => resolve(response.json()))
    })
}
